import { ChangeEvent, useState } from "react";
import { Form } from "react-bootstrap";
import TextField from "@components/FormsV2/TextField/TextField";
import MarkdownTextAreaV2 from "@components/FormsV2/MarkdownTextAreaV2";
import GenericToggle from "@components/FormsV2/GenericToggle/GenericToggle";
import type { ServiceConditionFormValues } from "../types";
import "./style.css";

const CONTENT_PLACEHOLDER =
  "The terms or conditions text shown on the document (Markdown supported)";

export interface ServiceConditionFormProps {
  id: string;
  initialValues: ServiceConditionFormValues;
  isSubmitting: boolean;
  onSubmit: (values: ServiceConditionFormValues) => void;
  onInvalid?: () => void;
}

const MIN_LEN = 3;

export const ServiceConditionForm = ({
  id,
  initialValues,
  isSubmitting,
  onSubmit,
  onInvalid,
}: ServiceConditionFormProps): JSX.Element => {
  const [values, setValues] =
    useState<ServiceConditionFormValues>(initialValues);

  const set = <K extends keyof ServiceConditionFormValues>(
    key: K,
    v: ServiceConditionFormValues[K]
  ) => setValues((prev) => ({ ...prev, [key]: v }));

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (
      values.name.trim().length < MIN_LEN ||
      values.content.trim().length < MIN_LEN
    ) {
      onInvalid?.();
      return;
    }
    onSubmit(values);
  };

  return (
    <Form id={id} data-testid={id} onSubmit={handleSubmit}>
      <div className="row">
        <div className="col-sm-6">
          <Form.Label htmlFor="service-condition-name">Name</Form.Label>
          <TextField
            label={null}
            id="service-condition-name"
            value={values.name}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              set("name", e.target.value)
            }
            required
          />
        </div>
        <div className="col-sm-6">
          <Form.Label>Required or Optional</Form.Label>
          <div className="serviceConditionToggleCell">
            <GenericToggle
              hasText
              onText="Required"
              offText="Optional"
              initialValueOfIsOn={values.status === "required"}
              onToggle={(on: boolean) =>
                set("status", on ? "required" : "optional")
              }
            />
          </div>
        </div>
      </div>

      <div className="row" style={{ paddingTop: "16px" }}>
        <div className="col">
          <Form.Label>Content (Supports Markdown)</Form.Label>
          <MarkdownTextAreaV2
            alwaysEdit
            value={values.content}
            placeholder={CONTENT_PLACEHOLDER}
            onChange={(md: string) => set("content", md)}
          />
        </div>
      </div>
    </Form>
  );
};

export default ServiceConditionForm;
